"use client";
import { cn } from "@/lib/utils";
import Link from "next/link";
import { useReducer } from "react";
import { Icon } from "./icon";
import { TooltipContent, TooltipRoot, TooltipTrigger } from "./ui/tooltip";

interface TooltipProps {
  title?: string;
  description?: string;
  linkLabel?: string;
  linkUrl?: string;
  className?: string;
  iconClass?: string;
  iconSize?: number;
}

export function Tooltip({
  title,
  description,
  linkLabel,
  linkUrl,
  className,
  iconClass = "stroke-primary",
  iconSize = 15,
}: TooltipProps) {
  const [isOpen, toggleIsOpen] = useReducer((open: boolean) => !open, false);
  return (
    <TooltipRoot open={isOpen} onOpenChange={toggleIsOpen}>
      <TooltipTrigger
        onClick={() => toggleIsOpen()}
        className={cn("cursor-pointer", className)}
      >
        <Icon icon="TbInfoCircle" size={iconSize} className={iconClass} />
      </TooltipTrigger>
      <TooltipContent side="bottom">
        <div className="flex justify-between items-center bg-primary px-3 py-2 text-white text-sm">
          {title}
          <button onClick={() => toggleIsOpen()}>
            <Icon
              icon="MdClose"
              size={14}
              className="fill-white cursor-pointer"
            />
          </button>
        </div>
        <div className="bg-white px-3 py-2 text-secondary text-xs">
          <span className="block mb-2">{description}</span>
          <Link
            href={`${linkUrl}`}
            className="text-primary hover:underline"
            prefetch={false}
            target="_blank"
          >
            {linkLabel}
          </Link>
        </div>
      </TooltipContent>
    </TooltipRoot>
  );
}

export function DisabledTooltip({
  description,
  className,
  children,
  forceClosed,
}: TooltipProps & { children: React.ReactNode; forceClosed?: boolean }) {
  const [isOpen, toggleIsOpen] = useReducer((open: boolean) => !open, false);
  if (!description) return <>{children}</>;
  return (
    <TooltipRoot open={isOpen && !forceClosed} onOpenChange={toggleIsOpen}>
      <TooltipTrigger className={cn("cursor-pointer", className)} asChild>
        {children}
      </TooltipTrigger>
      <TooltipContent side="left" disabled>
        <div className="bg-gray-300 px-3 py-2 text-secondary text-xs">
          {description}
        </div>
      </TooltipContent>
    </TooltipRoot>
  );
}
